import React, { PureComponent } from 'react';
import T from 'prop-types';
import Waveform from './components/waveform';
import Ruler from './components/ruler';
import Silences from './components/silences';
import Markers from './components/markers';
import Selection from './components/selection';
import Cursor from './components/cursor';
import { markerPropType } from '../../../../customPropTypes';

const PADDING_WIDTH = 42;
const MINIMUM_SELECTION_WIDTH = 5;

const deriveStateFromProps = ({ width, zoomLevel, episodeDuration }) => {
  const containerWidth = width - PADDING_WIDTH * 2;
  const timelineWidth = containerWidth * zoomLevel;
  const pixelsPerSecond = episodeDuration ? timelineWidth / episodeDuration : timelineWidth;
  const secondsPerPixel = episodeDuration / timelineWidth;

  return { containerWidth, timelineWidth, pixelsPerSecond, secondsPerPixel };
};

export default class Timeline extends PureComponent {
  state = {
    containerWidth: 0,
    timelineWidth: 0,
    pixelsPerSecond: 0,
    secondsPerPixel: 0,
    selection: undefined,
  };

  constructor(props) {
    super(props);
    this.state = deriveStateFromProps(props);
  }

  componentDidMount() {
    if (this.props.zoomLevel > 1) {
      this.scrollTimeline();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState(deriveStateFromProps(nextProps));
  }

  componentDidUpdate(prevProps, prevState) {
    const hasSelection = Boolean(this.state.selection);
    const hasNewAudioProgress = prevProps.audioProgress !== this.props.audioProgress;
    if (hasSelection && hasNewAudioProgress) {
      this.maybeRepeatAudioInSelection();
    }

    const hasNewZoomLevel = prevProps.zoomLevel !== this.props.zoomLevel;
    const hasNewTimelineWidth = prevState.timelineWidth !== this.state.timelineWidth;
    if (this.props.zoomLevel > 1 && (hasNewZoomLevel || hasNewTimelineWidth)) {
      this.scrollTimeline();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  }

  maybeRepeatAudioInSelection() {
    const [selectionStart, selectionEnd] = this.state.selection || [];
    if (this.props.audioProgress > selectionEnd) {
      this.props.onSeek(selectionStart);
    }
  }

  getScrollLeft = () => {
    return this.timelineRef ? this.timelineRef.scrollLeft : 0;
  };

  setScrollLeft = scrollLeft => {
    if (this.timelineRef) {
      this.timelineRef.scrollLeft = scrollLeft;
    }
  };

  getSortedSelection() {
    return this.state.selection && this.state.selection.sort((a, b) => a - b);
  }

  getTimeAtMiddleOfSelection() {
    const [selectionStart, selectionEnd] = this.state.selection;
    const selectionWidth = selectionEnd - selectionStart;
    const halfSelectionWidth = selectionWidth * 0.5;

    return selectionStart + halfSelectionWidth;
  }

  getTimeAtMiddleOfScreen() {
    const { containerWidth } = this.state;
    const { episodeDuration, zoomLevel } = this.props;
    const pixelAtMiddleOfScreen = this.getScrollLeft() + containerWidth * 0.5;
    const prevTimelineWidth = containerWidth * (zoomLevel - 1);

    return (pixelAtMiddleOfScreen / prevTimelineWidth) * episodeDuration;
  }

  getTimeToCenterSelectedMarkerOrMiddleOfScreen() {
    const { selectedMarkerId, markers } = this.props;
    if (selectedMarkerId) {
      const selectedMarker = markers.find(
        marker => selectedMarkerId && marker.id === selectedMarkerId
      );

      if (selectedMarker) {
        return selectedMarker.start;
      }
    }

    return this.getTimeAtMiddleOfScreen();
  }

  getTimeToCenter() {
    if (this.state.selection) {
      return this.getTimeAtMiddleOfSelection();
    }

    return this.getTimeToCenterSelectedMarkerOrMiddleOfScreen();
  }

  scrollTimeline() {
    const { containerWidth } = this.state;
    const pixelPerTime = (containerWidth / this.props.episodeDuration) * this.props.zoomLevel;
    const halfTimelineContainerWidth = containerWidth * 0.5;
    const scrollLeft = this.getTimeToCenter() * pixelPerTime - halfTimelineContainerWidth;

    this.setScrollLeft(scrollLeft);
  }

  clampSecondsToEpisode(seconds) {
    return Math.min(this.props.episodeDuration, Math.max(0, seconds));
  }

  getAudioTimeFromMousePosition(clientX) {
    const mouseLeft = clientX - PADDING_WIDTH;
    const mouseEventInTimelinePixels = mouseLeft + this.getScrollLeft();
    const mouseLeftInSeconds = mouseEventInTimelinePixels * this.state.secondsPerPixel;

    return this.clampSecondsToEpisode(mouseLeftInSeconds);
  }

  updateSelection(seconds) {
    const [currentStart] = this.state.selection || [];
    const nextStart = isNaN(currentStart) ? seconds : currentStart;
    const nextEnd = seconds;

    this.setState({ selection: [nextStart, nextEnd] });
  }

  didMarkerChangeStart(localMarker) {
    const remoteMarker = this.props.markers.find(marker => marker.id === localMarker.id);

    return remoteMarker.start !== localMarker.start;
  }

  handleMouseDown = () => {
    this.setState({ selection: undefined });
    this.props.onSelectMarker(undefined);

    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
  };

  handleMouseMove = event => {
    const audioTime = this.getAudioTimeFromMousePosition(event.clientX);
    this.updateSelection(audioTime);
  };

  handleMouseUp = event => {
    const { pixelsPerSecond } = this.state;
    const selection = this.getSortedSelection();
    if (selection) {
      const [selectionStart, selectionEnd] = selection;
      const selectionWidth = (selectionEnd - selectionStart) * pixelsPerSecond;
      if (selectionWidth < MINIMUM_SELECTION_WIDTH) {
        this.setState({ selection: undefined });
      } else {
        this.props.onSeek(selectionStart);
      }
    } else {
      const nextAudioTime = this.getAudioTimeFromMousePosition(event.clientX);
      this.props.onSeek(nextAudioTime);
    }

    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  };

  handleMarkerChange = (markerId, start) => {
    const { markers, onUpdateMarker } = this.props;
    const selectedMarker = markers.find(marker => marker.id === markerId);

    if (selectedMarker) {
      onUpdateMarker(selectedMarker.episode, markerId, { start });
    }
  };

  setRef = element => {
    this.timelineRef = element;
  };

  renderPadding() {
    return (
      <div
        className="editor-timeline-padding"
        data-test-id="EditorTimeline__padding"
        style={{ width: `${PADDING_WIDTH}px` }}
      />
    );
  }

  render() {
    const { timelineWidth, pixelsPerSecond, selection } = this.state;
    const {
      audioProgress,
      mediaUrl,
      episodeDuration,
      silenceDetected,
      selectedMarkerId,
      onSelectMarker,
    } = this.props;
    const containerStyle = {
      width: `${timelineWidth}px`,
    };

    return (
      <div
        className="editor-timeline-container"
        id="editor-timeline-container"
        data-test-id="EditorTimeline"
        onMouseDown={this.handleMouseDown}
        ref={this.setRef}
      >
        {this.renderPadding()}
        <div className="editor-timeline" id="acast-elm-editor-timeline" style={containerStyle}>
          <div className="editor-timeline-waveform-container">
            <Waveform
              width={timelineWidth}
              audioProgress={audioProgress}
              episodeDuration={episodeDuration}
              mediaUrl={mediaUrl}
            />
          </div>
          <Ruler episodeDuration={episodeDuration} timelineWidth={timelineWidth} />
          {silenceDetected && (
            <Silences silenceDetected={silenceDetected} pixelsPerSecond={pixelsPerSecond} />
          )}
          <Markers
            markers={this.props.markers}
            selectedMarkerId={selectedMarkerId}
            episodeDuration={episodeDuration}
            timelineWidth={timelineWidth}
            pixelsPerSecond={pixelsPerSecond}
            onSelect={onSelectMarker}
            onChange={this.handleMarkerChange}
          />
          {selection && <Selection range={selection} pixelsPerSecond={pixelsPerSecond} />}
          <Cursor audioProgress={audioProgress} pixelsPerSecond={pixelsPerSecond} />
        </div>
        {this.renderPadding()}
      </div>
    );
  }
}

Timeline.propTypes = {
  audioProgress: T.number.isRequired,
  episodeDuration: T.number,
  markers: T.arrayOf(markerPropType),
  mediaUrl: T.string,
  onSeek: T.func.isRequired,
  onSelectMarker: T.func.isRequired,
  onUpdateMarker: T.func.isRequired,
  width: T.number.isRequired,
  zoomLevel: T.number.isRequired,
  selectedMarkerId: T.string,
  silenceDetected: T.arrayOf(
    T.shape({
      end: T.number,
      duration: T.number,
    })
  ),
};

Timeline.defaultProps = {
  episodeDuration: 0,
  markers: [],
  mediaUrl: undefined,
  selectedMarkerId: undefined,
  silenceDetected: undefined,
};
